import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Users, Plus, ArrowLeft, Calendar, Pencil, X, Download, Check, FileText, Trash2, Search } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const MembersPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ first_name: '', last_name: '', date_of_birth: '', diagnosis: '', care_plan: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', date_of_birth: '', diagnosis: '', care_plan: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/auth'); return; }
    fetchMembers();
  }, [token]);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/members`, { headers });
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/members`, formData, { headers });
      setShowForm(false);
      setFormData({ first_name: '', last_name: '', date_of_birth: '', diagnosis: '', care_plan: '' });
      fetchMembers();
    } catch (error) {
      console.error('Failed to create member:', error);
    }
  };

  const startEdit = (member) => {
    setEditingId(member.id);
    setEditData({
      first_name: member.first_name,
      last_name: member.last_name,
      date_of_birth: member.date_of_birth,
      diagnosis: member.diagnosis || '',
      care_plan: member.care_plan || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({ first_name: '', last_name: '', date_of_birth: '', diagnosis: '', care_plan: '' });
  };

  const saveEdit = async (memberId) => {
    setSavingEdit(true);
    try {
      await axios.put(`${API}/members/${memberId}`, editData, { headers });
      setEditingId(null);
      fetchMembers();
    } catch (error) {
      console.error('Failed to update member:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSavingEdit(false);
    }
  };

  const downloadPdf = async (memberId) => {
    try {
      const response = await axios.get(`${API}/members/${memberId}/pdf`, { headers, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `beneficiaire-${memberId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 100);
    } catch (error) {
      console.error('PDF download failed:', error);
      window.open(`${API}/members/${memberId}/pdf?token=${token}`, '_blank');
    }
  };

  const downloadAllNotesPdf = async (memberId, memberName) => {
    try {
      const response = await axios.get(`${API}/members/${memberId}/notes/pdf`, { headers, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `notes-${memberName.replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 100);
    } catch (error) {
      if (error.response?.status === 404) {
        alert('Aucune note trouvée pour ce bénéficiaire');
      } else {
        console.error('PDF download failed:', error);
        window.open(`${API}/members/${memberId}/notes/pdf?token=${token}`, '_blank');
      }
    }
  };

  const deleteMember = async (memberId, memberName) => {
    if (!window.confirm(`Supprimer ${memberName} et toutes ses données associées (notes, RDV) ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/members/${memberId}`, { headers });
      fetchMembers();
    } catch (error) {
      console.error('Failed to delete member:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const filteredMembers = members.filter(m => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      m.first_name.toLowerCase().includes(q) ||
      m.last_name.toLowerCase().includes(q) ||
      (m.diagnosis || '').toLowerCase().includes(q) ||
      (m.care_plan || '').toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <Heart className="w-12 h-12 text-primary fill-primary mx-auto mb-4 animate-pulse" />
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
          <div>
            <button
              data-testid="back-to-dashboard"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-primary mb-3 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au tableau de bord
            </button>
            <h1 className="text-2xl md:text-3xl font-bold text-primary-900 font-heading">Bénéficiaires</h1>
          </div>
          <Button data-testid="add-member-btn" onClick={() => setShowForm(!showForm)} className="w-full sm:w-auto">
            <Plus className="w-5 h-5 mr-2" />
            {showForm ? 'Annuler' : 'Ajouter'}
          </Button>
        </div>

        {!showForm && members.length > 0 && (
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="member-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par nom, diagnostic, plan..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
            />
          </div>
        )}

        {showForm && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Card>
              <CardHeader><CardTitle>Nouveau bénéficiaire</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Prénom</label>
                      <Input data-testid="member-firstname-input" name="first_name" value={formData.first_name} onChange={handleChange} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Nom</label>
                      <Input data-testid="member-lastname-input" name="last_name" value={formData.last_name} onChange={handleChange} required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Date de naissance</label>
                    <Input data-testid="member-dob-input" type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Diagnostic (optionnel)</label>
                    <Input data-testid="member-diagnosis-input" name="diagnosis" value={formData.diagnosis} onChange={handleChange} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Plan d'accompagnement (optionnel)</label>
                    <Textarea data-testid="member-careplan-input" name="care_plan" value={formData.care_plan} onChange={handleChange} />
                  </div>
                  <Button data-testid="submit-member-btn" type="submit" className="w-full">Enregistrer</Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {members.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Aucun bénéficiaire</h3>
              <p className="text-slate-600 mb-6">Commencez par ajouter votre premier bénéficiaire</p>
              <Button data-testid="empty-add-member-btn" onClick={() => setShowForm(true)}>
                <Plus className="w-5 h-5 mr-2" />
                Ajouter un bénéficiaire
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
          {filteredMembers.length === 0 && searchQuery ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Aucun bénéficiaire ne correspond à votre recherche</p>
              </CardContent>
            </Card>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMembers.map((member) => (
              <motion.div key={member.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Card data-testid={`member-card-${member.id}`} className="hover:-translate-y-1">
                  <CardContent className="p-4 md:p-6">
                    {editingId === member.id ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <Input data-testid={`edit-member-firstname-${member.id}`} value={editData.first_name} onChange={(e) => setEditData({ ...editData, first_name: e.target.value })} placeholder="Prénom" />
                          <Input data-testid={`edit-member-lastname-${member.id}`} value={editData.last_name} onChange={(e) => setEditData({ ...editData, last_name: e.target.value })} placeholder="Nom" />
                        </div>
                        <Input data-testid={`edit-member-dob-${member.id}`} type="date" value={editData.date_of_birth} onChange={(e) => setEditData({ ...editData, date_of_birth: e.target.value })} />
                        <Input data-testid={`edit-member-diagnosis-${member.id}`} value={editData.diagnosis} onChange={(e) => setEditData({ ...editData, diagnosis: e.target.value })} placeholder="Diagnostic" />
                        <Textarea data-testid={`edit-member-careplan-${member.id}`} value={editData.care_plan} onChange={(e) => setEditData({ ...editData, care_plan: e.target.value })} placeholder="Plan d'accompagnement" rows={3} />
                        <div className="flex gap-2">
                          <Button data-testid={`save-edit-member-${member.id}`} size="sm" onClick={() => saveEdit(member.id)} disabled={savingEdit}>
                            <Check className="w-4 h-4 mr-1" />{savingEdit ? 'Sauvegarde...' : 'Sauvegarder'}
                          </Button>
                          <Button data-testid={`cancel-edit-member-${member.id}`} size="sm" variant="outline" onClick={cancelEdit}>
                            <X className="w-4 h-4 mr-1" />Annuler
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start gap-4 mb-4">
                          <div className="p-3 bg-primary-50 rounded-full shrink-0">
                            <Users className="w-6 h-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-bold text-primary-900">{member.first_name} {member.last_name}</h3>
                            <div className="flex items-center gap-2 text-sm text-slate-600 mt-1">
                              <Calendar className="w-4 h-4" />
                              {member.date_of_birth}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button data-testid={`edit-member-btn-${member.id}`} onClick={() => startEdit(member)} className="p-1.5 hover:bg-primary-50 rounded-lg transition-colors" title="Modifier">
                              <Pencil className="w-4 h-4 text-slate-400 hover:text-primary" />
                            </button>
                            <button data-testid={`download-member-pdf-${member.id}`} onClick={() => downloadPdf(member.id)} className="p-1.5 hover:bg-primary-50 rounded-lg transition-colors" title="Télécharger la fiche">
                              <Download className="w-4 h-4 text-slate-400 hover:text-primary" />
                            </button>
                            <button data-testid={`download-member-notes-pdf-${member.id}`} onClick={() => downloadAllNotesPdf(member.id, `${member.first_name} ${member.last_name}`)} className="p-1.5 hover:bg-primary-50 rounded-lg transition-colors" title="Exporter toutes les notes">
                              <FileText className="w-4 h-4 text-slate-400 hover:text-secondary" />
                            </button>
                            <button data-testid={`delete-member-${member.id}`} onClick={() => deleteMember(member.id, `${member.first_name} ${member.last_name}`)} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" title="Supprimer">
                              <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                            </button>
                          </div>
                        </div>
                        {member.diagnosis && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-slate-700 mb-1">Diagnostic</p>
                            <p className="text-sm text-slate-600">{member.diagnosis}</p>
                          </div>
                        )}
                        {member.care_plan && (
                          <div>
                            <p className="text-sm font-medium text-slate-700 mb-1">Plan d'accompagnement</p>
                            <p className="text-sm text-slate-600 line-clamp-3">{member.care_plan}</p>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default MembersPage;
